import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { ManageHeader, SearchBar, Fab, EmptyState, Field, TextInput, TextArea, SelectInput } from '../../components/mobile/ManageKit';
import { MapPin, Trash2, Loader2 } from 'lucide-react';

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

const MobileManageSites = () => {
  const { api, can } = useMobileAuth();
  const { customerId } = useParams();
  const location = useLocation();
  const customerName = location.state?.customerName || 'Customer';
  const empty = { name: '', customer_id: customerId, street_address: '', suburb: '', postcode: '', state: '', notes: '' };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/sites');
      setItems((res.data || []).filter((s) => s.customer_id === customerId));
    } catch (e) {
      toast.error('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [customerId]);

  const openNew = () => { setEditing(null); setForm(empty); setConfirmDel(false); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v != null)) }); setConfirmDel(false); setOpen(true); };

  const save = async () => {
    const hasName = (form.name || '').trim();
    const hasAddr = (form.street_address || '').trim() && (form.suburb || '').trim();
    if (!hasName && !hasAddr) return toast.error('Enter a site name or street address + suburb');
    setSaving(true);
    try {
      const payload = {
        name: form.name || null,
        customer_id: customerId,
        street_address: form.street_address || null,
        suburb: form.suburb || null,
        postcode: form.postcode || null,
        state: form.state || null,
        notes: form.notes || null,
      };
      if (editing) await api.put(`/api/sites/${editing.id}`, payload);
      else await api.post('/api/sites', payload);
      toast.success(editing ? 'Site updated' : 'Site added');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/api/sites/${editing.id}`);
      toast.success('Site deleted');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot delete');
    }
  };

  const siteLabel = (s) => s.name || [s.street_address, s.suburb].filter(Boolean).join(', ') || 'Unnamed site';
  const filtered = items.filter((s) => siteLabel(s).toLowerCase().includes(q.toLowerCase()));

  return (
    <MobileLayout>
      <ManageHeader title="Sites" subtitle={customerName} backTo="/mobile/manage/customers" />
      <div className="p-4 space-y-3">
        <SearchBar value={q} onChange={setQ} placeholder="Search sites..." />
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-[#ffd108] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No sites" subtitle="Tap Add to create a site for this customer" />
        ) : (
          <div className="space-y-2" data-testid="sites-list">
            {filtered.map((s) => (
              <button key={s.id} onClick={() => openEdit(s)}
                className="w-full flex items-center gap-3 bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg px-4 py-3 text-left active:bg-[var(--m-bg-elevated)]"
                data-testid={`site-row-${s.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[var(--m-bg)] flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--m-text)] font-medium truncate">{s.name || siteLabel(s)}</div>
                  {s.name && <div className="text-xs text-[var(--m-text-secondary)] truncate">{[s.street_address, s.suburb, s.state].filter(Boolean).join(', ')}</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <Fab onClick={openNew} label="Add" testId="site-add-fab" visible={can('sites.write')} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)] rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader><SheetTitle className="text-[var(--m-text)] text-left">{editing ? 'Edit Site' : 'New Site'}</SheetTitle></SheetHeader>
          <div className="py-4 space-y-3">
            <Field label="Site name" hint="Name or address required"><TextInput testId="site-name" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Level 5 Tower Project" /></Field>
            <Field label="Street address"><TextInput testId="site-street" value={form.street_address || ''} onChange={(e) => setForm({ ...form, street_address: e.target.value })} placeholder="123 George St" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Suburb"><TextInput testId="site-suburb" value={form.suburb || ''} onChange={(e) => setForm({ ...form, suburb: e.target.value })} /></Field>
              <Field label="Postcode"><TextInput testId="site-postcode" value={form.postcode || ''} onChange={(e) => setForm({ ...form, postcode: e.target.value })} /></Field>
            </div>
            <Field label="State">
              <SelectInput testId="site-state" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })}
                placeholder="—" options={STATES.map((s) => ({ value: s, label: s }))} />
            </Field>
            <Field label="Notes"><TextArea testId="site-notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <div className="space-y-2 pb-2">
            <Button className="btn-volt w-full" onClick={save} disabled={saving} data-testid="site-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Add site')}
            </Button>
            {editing && (
              confirmDel ? (
                <Button variant="destructive" className="w-full" onClick={remove} data-testid="site-delete-confirm">Tap again to confirm delete</Button>
              ) : (
                <Button variant="outline" className="w-full border-red-800 text-red-400" onClick={() => setConfirmDel(true)} data-testid="site-delete">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
};

export default MobileManageSites;
